import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { HiArrowRight, HiStar } from "react-icons/hi";

import { Headshot } from "MVVM/Views/TopPlayers/Components/Headshot";
import { useFeaturedPros } from "MVVM/Views/TopPlayers/ViewModel";

const FACES = 3;
const NAMED = 2;

interface IProContentLinkProps {
  label: string;
  onClick?: () => void;
  to: string;
}

// The way into the Top Players hub from elsewhere on the site: featured pros'
// faces stand in for an icon, so the link shows what is behind it.
export const ProContentLink = ({
  label,
  onClick,
  to,
}: IProContentLinkProps) => {
  const { pros } = useFeaturedPros();
  const faces = pros.slice(0, FACES);
  const names = faces.slice(0, NAMED).map((pro) => pro.name);

  return (
    <Row to={to} onClick={onClick}>
      {faces.length ? (
        <Faces>
          {faces.map((pro) => (
            <Face key={pro.id}>
              <Headshot name={pro.name} size={36} src={pro.avatar} />
            </Face>
          ))}
        </Faces>
      ) : (
        <Tile>
          <HiStar aria-hidden size={18} />
        </Tile>
      )}
      <Copy>
        <Label>{label}</Label>
        {names.length > 0 && (
          <Names>Clips from {names.join(", ")} and more</Names>
        )}
      </Copy>
      <Arrow aria-hidden>
        <HiArrowRight size={16} />
      </Arrow>
    </Row>
  );
};

interface IProPlayerLinkProps {
  avatar?: string | null;
  label: string;
  name: string;
  to: string;
}

// The quieter link to one pro's Top Players page, for pages already about them.
export const ProPlayerLink = ({
  avatar,
  label,
  name,
  to,
}: IProPlayerLinkProps) => (
  <PlayerRow to={to}>
    <Headshot name={name} size={24} src={avatar} />
    {label}
    <Arrow aria-hidden>
      <HiArrowRight size={16} />
    </Arrow>
  </PlayerRow>
);

const Arrow = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-shrink: 0;
  transition:
    color 0.15s ease,
    transform 0.2s ease;
`;

// The ring matches the row behind it, so overlapping faces read as separate.
const Face = styled.span`
  border-radius: 50%;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.navy200};
  line-height: 0;
  margin-left: -10px;
  transition: box-shadow 0.15s ease;
`;

const linkSurface = css`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow, sans-serif;
  max-width: 100%;
  text-decoration: none;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.navy100};
    border-color: ${({ theme }) => theme.colors.envy};
  }

  &:hover ${Arrow} {
    color: ${({ theme }) => theme.colors.envy};
    transform: translateX(3px);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    ${Arrow} {
      transition: none;
    }
  }
`;

// Hugs its text even inside a stretching column.
const PlayerRow = styled(Link)`
  ${linkSurface}
  align-self: flex-start;
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  padding: 7px 12px 7px 8px;
`;

const Row = styled(Link)`
  ${linkSurface}
  display: flex;
  gap: 12px;
  padding: 10px 14px 10px 10px;

  &:hover ${Face} {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.navy100};
  }
`;

const Faces = styled.span`
  display: flex;
  flex-shrink: 0;
  padding-left: 10px;
`;

const Tile = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.envyAlpha10};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  flex-shrink: 0;
  height: 36px;
  justify-content: center;
  width: 36px;
`;

const Copy = styled.span`
  display: flex;
  flex: 1;
  flex-direction: column;
  line-height: 1.2;
  min-width: 0;
`;

const Label = styled.span`
  font-size: 16px;
  font-weight: 700;
`;

const Names = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
  text-wrap: balance;
`;
